import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { PropTypes } from "prop-types";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { identifiersData, inputData, namesData } from "@/data";
import {
    StyledThumbnailPreview,
    StyledThumbnailPreviewImage,
    StyledThumbnailPreviewLoader,
    StyledThumbnailPreviewPlaceholder,
} from "@/styles";

const ThumbnailPreview = ({
    accept,
    canDelete,
    canUpload,
    deleteTooltip,
    downloadLink,
    id,
    loading,
    onDelete,
    onFileSelect,
    rowId,
    src,
}) => {
    const inputRef = useRef();

    const { t } = useTranslation();

    const { button: buttonId, input: inputId, thumbnail: thumbnailId } = identifiersData.ids;

    const {
        delete: deleteButtonName,
        download: downloadButtonName,
        edit: editButtonName,
        upload: uploadButtonName,
    } = namesData.buttons;

    const idPrefix = `${id}_${thumbnailId}${rowId ? `_${rowId}` : ""}`;

    const openFilePickerHandler = () => inputRef.current?.click();

    return (
        <StyledThumbnailPreview id={idPrefix}>
            <Box sx={{ position: "relative" }}>
                {src ? (
                    <StyledThumbnailPreviewImage
                        alt={t("imageAlternatives.videoThumbnail")}
                        loading="lazy"
                        src={`${import.meta.env.VITE_BACKEND_DOMAIN}${src}`}
                    />
                ) : (
                    <Tooltip title={t("videos.uploadThumbnail")}>
                        <span>
                            <StyledThumbnailPreviewPlaceholder
                                aria-label={t("videos.uploadThumbnail")}
                                disabled={!canUpload || loading}
                                id={`${idPrefix}_${uploadButtonName}_${buttonId}`}
                                onClick={openFilePickerHandler}
                            >
                                <AddPhotoAlternateIcon color="primary" fontSize="large" />
                            </StyledThumbnailPreviewPlaceholder>
                        </span>
                    </Tooltip>
                )}
                {loading && (
                    <StyledThumbnailPreviewLoader>
                        <CircularProgress size={24} />
                    </StyledThumbnailPreviewLoader>
                )}
            </Box>
            {src && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-around",
                    }}
                >
                    <Tooltip title={t("videos.editThumbnail")}>
                        <span>
                            <IconButton
                                aria-label={t("videos.editThumbnail")}
                                disabled={!canUpload || loading}
                                id={`${idPrefix}_${editButtonName}_${buttonId}`}
                                onClick={openFilePickerHandler}
                            >
                                <EditOutlinedIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={t("actions.download")}>
                        <IconButton
                            aria-label={t("actions.download")}
                            component="a"
                            href={`${import.meta.env.VITE_BACKEND_DOMAIN}${downloadLink}`}
                            id={`${idPrefix}_${downloadButtonName}_${buttonId}`}
                            rel="noreferrer"
                            target="_blank"
                            download
                        >
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={deleteTooltip}>
                        <span>
                            <IconButton
                                aria-label={deleteTooltip}
                                disabled={!canDelete || loading}
                                id={`${idPrefix}_${deleteButtonName}_${buttonId}`}
                                onClick={onDelete}
                            >
                                <DeleteForeverOutlinedIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            )}
            <input
                accept={accept}
                id={`${idPrefix}_${inputId}`}
                ref={inputRef}
                type={inputData.types.file}
                hidden
                onChange={(e) => {
                    const file = e.target.files[0];

                    // reset so picking the same file again still triggers a change
                    e.target.value = "";

                    if (file) onFileSelect(file);
                }}
            />
        </StyledThumbnailPreview>
    );
};

export default ThumbnailPreview;

ThumbnailPreview.propTypes = {
    accept: PropTypes.string,
    canDelete: PropTypes.bool,
    canUpload: PropTypes.bool,
    deleteTooltip: PropTypes.string,
    downloadLink: PropTypes.string,
    id: PropTypes.string,
    loading: PropTypes.bool,
    onDelete: PropTypes.func,
    onFileSelect: PropTypes.func,
    rowId: PropTypes.number,
    src: PropTypes.string,
};
